import { useState, useEffect, useRef } from "react";
import { Bell, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

const NotificationBell = ({ userId }: { userId: string }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    const fetch = async () => {
      // Soft-deleted rows (dismissed_at IS NOT NULL) are intentionally
      // hidden from the bell. The column was added in migration
      // 20260519000002_notifications_dismissed_at.sql.
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", userId)
        .is("dismissed_at", null)
        .order("created_at", { ascending: false })
        .limit(20);
      if (data) setNotifications(data);
    };
    fetch();

    // Realtime subscription
    const channel = supabase
      .channel("user-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const fresh = payload.new as Notification & { dismissed_at?: string | null };
          // Defensive — a brand-new row should never have dismissed_at
          // set, but skip just in case the INSERT carried it.
          if (fresh.dismissed_at) return;
          setNotifications((prev) => [fresh as Notification, ...prev].slice(0, 20));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const markAsRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
    );
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase
      .from("notifications")
      .update({ is_read: true })
      .in("id", unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  // Soft-delete a single row. The optimistic update yanks the row out
  // of state immediately; AnimatePresence in the render tree handles
  // the height collapse. If the network write fails we still leave the
  // row dismissed in the UI — admin tooling can rescue from the DB if
  // ever needed.
  const dismissOne = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    await supabase
      .from("notifications")
      .update({
        dismissed_at: new Date().toISOString(),
        is_read: true,
      })
      .eq("id", id);
  };

  const clearAll = async () => {
    if (notifications.length === 0) return;
    const ids = notifications.map((n) => n.id);
    setNotifications([]);
    await supabase
      .from("notifications")
      .update({
        dismissed_at: new Date().toISOString(),
        is_read: true,
      })
      .in("id", ids);
  };

  const inferLinkFromNotification = (title: string, type: string): string => {
    if (title.includes("Information Needed")) return "/marketplace/messages";
    if (title.includes("Application Approved")) return "/fleet-manager/dashboard";
    if (title === "Application Not Approved") return "/fleet-manager-apply";
    // Management-request notifications split by audience. "New management
    // request" is what FindFleetManager inserts when an owner submits a
    // request, so the recipient is always the fleet manager — route them
    // straight to the requests tab on the FM dashboard. The capitalised
    // "Management Request Accepted" / "Fleet Management Confirmed" titles
    // are the owner-side flow and still belong on /marketplace/dashboard.
    if (title.toLowerCase().includes("new management request"))
      return "/fleet-manager/dashboard?tab=requests";
    if (title.includes("Management Request") || title.includes("Fleet Management"))
      return "/marketplace/dashboard";
    if (title.includes("Message")) return "/marketplace/messages";
    if (title.includes("Modification")) return "/marketplace/dashboard?tab=requests";
    if (title.includes("Verification"))
      return "/marketplace/dashboard?tab=verification";
    if (title.includes("Dispute")) return "/marketplace/dashboard?tab=claims";
    if (title.includes("Deposit")) return "/marketplace/dashboard?tab=payments";
    if (title.includes("Payout")) return "/marketplace/dashboard?tab=earnings";
    if (title.includes("Insurance"))
      return "/marketplace/dashboard?tab=verification";
    if (title.includes("Performance Report")) return "/fleet-manager/dashboard";
    if (title.includes("Referral")) return "/marketplace/dashboard?tab=referrals";
    if (title.includes("Promotion")) return "/marketplace/dashboard?tab=promotions";
    if (type === "claim") return "/marketplace/dashboard?tab=claims";
    if (type === "payment" || type === "payout")
      return "/marketplace/dashboard?tab=payments";
    if (type === "verification")
      return "/marketplace/dashboard?tab=verification";
    // Trip reminders — deep link to active trips
    if (
      title.includes("2 Hours") ||
      title.includes("2-Hour") ||
      title.includes("Reminder") ||
      title.includes("Coming Soon") ||
      title.includes("Trip Starting")
    )
      return "/marketplace/dashboard?tab=active";
    if (title.includes("Picked Up") || title.includes("Check-In"))
      return "/marketplace/dashboard?tab=active-trips";
    if (
      title.includes("Booking") ||
      title.includes("Trip") ||
      title.includes("Pickup") ||
      title.includes("Return")
    )
      return "/marketplace/dashboard?tab=requests";
    if (title.includes("Review")) return "/marketplace/dashboard?tab=reviews";
    if (title.includes("Error") || title.includes("Critical"))
      return "/admin?tab=errors";
    return "/marketplace/dashboard";
  };

  const typeIcon: Record<string, string> = {
    booking: "📋",
    approval: "✅",
    decline: "❌",
    reminder: "⏰",
    review: "⭐",
    payout: "💰",
    info: "ℹ️",
    success: "✅",
    warning: "⚠️",
    error: "❌",
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        aria-label="Notifications"
        className="relative flex items-center text-muted-foreground hover:text-foreground transition-colors duration-300"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#B8960C] text-[#0A0A0A] text-[9px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            // Heavy fill + bronze hairline + tall shadow so the panel
            // lifts cleanly off cream dashboard surfaces. backdrop-blur
            // stays on for a hint of context behind it.
            //
            // Mobile fix: w-80 = 320px equals iPhone SE viewport so the
            // dropdown touched both screen edges. Mobile override below
            // (in the <style> block at end of return) clamps the width
            // to calc(100vw - 32px) at <768px so there's always a 16px
            // gutter. Desktop (md:w-96 = 384px) stays untouched.
            id="vauren-notification-dropdown"
            className="absolute right-0 top-full mt-3 w-80 md:w-96 rounded-2xl z-50 max-h-[420px] md:max-h-[520px] flex flex-col overflow-hidden"
            style={{
              background: "rgba(30, 24, 20, 0.97)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(153, 126, 84, 0.25)",
              boxShadow: "0 8px 48px rgba(0, 0, 0, 0.4)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <span className="text-[10px] font-medium tracking-[0.2em] uppercase text-white/60">
                Notifications
              </span>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] text-[#B8960C] hover:underline"
                  >
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-[10px] text-white/45 hover:text-white/80 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="py-10 px-6 flex flex-col items-center text-center">
                  <div
                    aria-hidden
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: "rgba(184,150,12,0.18)",
                      color: "#B8960C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: 22,
                      letterSpacing: "0.02em",
                      marginBottom: 12,
                    }}
                  >
                    V
                  </div>
                  <p
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: 18,
                      color: "#FFFFFF",
                      fontWeight: 400,
                      margin: 0,
                      letterSpacing: "0.01em",
                    }}
                  >
                    You are all caught up.
                  </p>
                  <p
                    style={{
                      fontFamily: '"DM Sans", system-ui, sans-serif',
                      fontSize: 12,
                      color: "rgba(255,255,255,0.55)",
                      margin: "6px 0 0",
                      lineHeight: 1.5,
                    }}
                  >
                    New activity will appear here.
                  </p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {notifications.map((n) => (
                    <motion.div
                      key={n.id}
                      layout
                      initial={{ opacity: 1, height: "auto" }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        paddingTop: 0,
                        paddingBottom: 0,
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className={cn(
                        "border-b border-white/[0.04] hover:bg-white/[0.04] transition-colors cursor-pointer overflow-hidden",
                        !n.is_read && "bg-[#B8960C]/8",
                      )}
                      onClick={() => {
                        markAsRead(n.id);
                        setOpen(false);
                        const destination =
                          n.link || inferLinkFromNotification(n.title, n.type);
                        if (destination) {
                          const current =
                            window.location.pathname + window.location.search;
                          if (current === destination) {
                            window.location.reload();
                          } else {
                            navigate(destination);
                          }
                        }
                      }}
                    >
                      <div className="px-4 py-3 flex items-start gap-2.5">
                        <span className="text-sm mt-0.5">
                          {typeIcon[n.type] || "📌"}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              "text-xs truncate text-white",
                              !n.is_read && "font-medium",
                            )}
                          >
                            {n.title}
                          </p>
                          <p
                            className="text-[11px] line-clamp-2 mt-0.5"
                            style={{ color: "rgba(255,255,255,0.7)" }}
                          >
                            {n.message}
                          </p>
                          <p
                            className="text-[10px] mt-1"
                            style={{ color: "rgba(255,255,255,0.4)" }}
                          >
                            {formatDistanceToNow(new Date(n.created_at), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                        <div className="flex flex-col items-center gap-2 mt-0.5">
                          {!n.is_read && (
                            <span className="w-2 h-2 bg-[#B8960C] rounded-full flex-shrink-0" />
                          )}
                          <button
                            type="button"
                            onClick={(e) => dismissOne(n.id, e)}
                            aria-label="Dismiss notification"
                            className="flex items-center justify-center transition-colors"
                            style={{
                              width: 20,
                              height: 20,
                              color: "rgba(255,255,255,0.5)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = "rgba(255,255,255,1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                            }}
                          >
                            <X size={14} strokeWidth={2.2} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Mobile-only width clamp. Desktop md:w-96 stays untouched. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media (max-width: 767px) {
              #vauren-notification-dropdown {
                width: calc(100vw - 32px) !important;
                max-width: 20rem;
                right: 0 !important;
              }
            }
          `,
        }}
      />
    </div>
  );
};

export default NotificationBell;
